import { parseAuthorIdentity } from "./authorDisplayName";

const teamRegistry = new WeakSet();

function normalizeAuthorKey(value = "") {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function applyAuthorDisplayFields(item) {
  const trigger = item.querySelector("[data-team-person-trigger]");

  if (!trigger) {
    return;
  }

  const parsed = parseAuthorIdentity(trigger.dataset.teamPreviewName, trigger.dataset.teamPreviewLocation);
  const displayTitle = parsed.designation;

  trigger.dataset.teamPreviewName = parsed.name;
  trigger.dataset.teamPreviewTitle = displayTitle;
  trigger.dataset.teamPreviewLocation = parsed.location;

  item.querySelectorAll("[data-team-person-location]").forEach((element) => {
    element.textContent = parsed.location;
  });

  item.querySelectorAll("[data-team-person-location-row]").forEach((element) => {
    element.hidden = !parsed.location;
  });

  item.querySelectorAll("[data-team-person-name]").forEach((element) => {
    element.textContent = parsed.name;
  });

  item.querySelectorAll("[data-team-person-title]").forEach((element) => {
    element.textContent = displayTitle;
  });

  item.querySelectorAll("[data-team-person-image]").forEach((image) => {
    image.alt = `${parsed.name} portrait`;
  });
}

function bindTeamPage(page) {
  if (teamRegistry.has(page)) {
    return;
  }

  const authorDirectory = page.querySelector("[data-team-author-order]");
  const authorOrder = (authorDirectory?.dataset.teamAuthorOrder || "")
    .split(",")
    .map((slug) => slug.trim().toLowerCase())
    .filter(Boolean);

  const allAuthorItems = authorDirectory
    ? [...authorDirectory.querySelectorAll("[data-team-person-item]")]
    : [];

  allAuthorItems.forEach(applyAuthorDisplayFields);

  if (authorDirectory && authorOrder.length) {
    const itemsByKey = new Map();

    allAuthorItems.forEach((item) => {
      const trigger = item.querySelector("[data-team-person-trigger]");
      const authorSlug = normalizeAuthorKey(item.dataset.teamPersonItem);
      const authorName = normalizeAuthorKey(trigger?.dataset.teamPreviewName);

      if (authorSlug) {
        itemsByKey.set(authorSlug, item);
      }

      if (authorName) {
        itemsByKey.set(authorName, item);
      }
    });

    const visibleItems = authorOrder
      .map((slug) => itemsByKey.get(normalizeAuthorKey(slug)))
      .filter((item, index, items) => item && items.indexOf(item) === index);

    authorDirectory.replaceChildren(...visibleItems);
  }

  if (authorDirectory) {
    authorDirectory.classList.add("is-author-order-ready");
  }

  const triggers = [...page.querySelectorAll("[data-team-person-trigger]")];
  const panels = [...page.querySelectorAll("[data-team-person-panel]")];
  const spotlightImage = page.querySelector("[data-team-spotlight-image]");
  const spotlight = page.querySelector("[data-team-spotlight]");
  const spotlightTitle = page.querySelector("[data-team-spotlight-title]");
  const spotlightLocation = page.querySelector("[data-team-spotlight-location]");
  const spotlightName = page.querySelector("[data-team-spotlight-name]");
  const spotlightLink = page.querySelector("[data-team-spotlight-link]");
  if (!triggers.length || !panels.length) {
    return;
  }

  let activeId =
    triggers.find((trigger) => trigger.classList.contains("is-active"))?.dataset.teamPersonTrigger ||
    triggers[0].dataset.teamPersonTrigger;
  let previewId = null;
  let lockedId = null;

  const renderSpotlight = (id) => {
    const trigger = triggers.find((item) => item.dataset.teamPersonTrigger === id);

    if (!trigger) {
      return;
    }

    if (spotlightImage) {
      const imageUrl = trigger.dataset.teamPreviewImage;

      if (imageUrl) {
        spotlightImage.src = imageUrl;
        spotlightImage.classList.remove("is-missing");
      } else {
        spotlightImage.removeAttribute("src");
        spotlightImage.classList.add("is-missing");
      }

      spotlightImage.alt = `${trigger.dataset.teamPreviewName || ""} portrait`.trim();
    }

    if (spotlightTitle) {
      spotlightTitle.textContent = trigger.dataset.teamPreviewTitle || "";
    }

    if (spotlightLocation) {
      spotlightLocation.textContent = trigger.dataset.teamPreviewLocation || "";
    }

    if (spotlightName) {
      spotlightName.textContent = trigger.dataset.teamPreviewName || "";
    }

    if (spotlightLink) {
      spotlightLink.href = trigger.dataset.teamProfileUrl || "#";
      spotlightLink.setAttribute(
        "aria-label",
        `View ${trigger.dataset.teamPreviewName || "team member"} profile`,
      );
    }
  };

  const updateUI = (nextId, { previewOnly = false } = {}) => {
    const displayId = previewOnly ? nextId : activeId;
    previewId = previewOnly ? nextId : null;

    triggers.forEach((trigger) => {
      const isCurrent = trigger.dataset.teamPersonTrigger === displayId;
      trigger.classList.toggle("is-active", isCurrent && !previewOnly);
      trigger.classList.toggle("is-preview", isCurrent && previewOnly);
      trigger.classList.toggle("is-locked", !previewOnly && lockedId === trigger.dataset.teamPersonTrigger);
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.teamPersonPanel === activeId;
      panel.classList.toggle("is-active", isActive);
      panel.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    renderSpotlight(displayId);
  };

  triggers.forEach((trigger) => {
    const triggerId = trigger.dataset.teamPersonTrigger;

    trigger.addEventListener("mouseenter", () => updateUI(triggerId, { previewOnly: true }));
    trigger.addEventListener("focus", () => updateUI(triggerId, { previewOnly: true }));

    trigger.addEventListener("mouseleave", (event) => {
      if (previewId === triggerId) {
        if (spotlight?.contains(event.relatedTarget)) {
          trigger.classList.remove("is-preview");
          return;
        }
        updateUI(activeId);
      }
    });
    trigger.addEventListener("blur", (event) => {
      if (previewId === triggerId) {
        if (spotlight?.contains(event.relatedTarget)) {
          trigger.classList.remove("is-preview");
          return;
        }
        updateUI(activeId);
      }
    });

    const lockAuthor = () => {
      activeId = triggerId;
      lockedId = triggerId;
      updateUI(activeId);
    };

    trigger.addEventListener("click", (event) => {
      if (!event.target.closest("a")) {
        lockAuthor();
      }
    });

    trigger.addEventListener("keydown", (event) => {
      if ((event.key === "Enter" || event.key === " ") && !event.target.closest("a")) {
        event.preventDefault();
        lockAuthor();
      }
    });
  });

  spotlight?.addEventListener("mouseleave", () => {
    if (previewId) {
      updateUI(activeId);
    }
  });

  spotlight?.addEventListener("focusout", (event) => {
    if (previewId && !spotlight.contains(event.relatedTarget)) {
      updateUI(activeId);
    }
  });

  updateUI(activeId);
  teamRegistry.add(page);
}

export default function initTeamDev(root = document) {
  root.querySelectorAll("[data-team-dev]").forEach(bindTeamPage);
}
